import { Injectable } from "@angular/core";
import { Observable } from "rxjs/internal/Observable";
import { environment } from "../../../environments/environment";
import { HttpClient } from "@angular/common/http";
import { GenericResponseDto } from "../models/generic-response.dto";
import { ApiCacheService } from "./api-cache.service";
import { FileUploadDto } from "../models/shared/fileUploadDto";
import { lastValueFrom } from "rxjs";
import { AddressFormats } from "../models/vendor/addVendor/addressFormat";
import { ClientBillingDto } from "../models/client/clientBillingDto";
import { GetProgramDetailsEmailDto } from "../models/program/getProgramDetailsForEmailDto";
import { ZipCodeDetails } from "../models/shared/zipCodeDetails";
import { Client } from "../models/client/details/client";
import { ClientAccountNumberResponse } from "../models/client/clientAccountNumberResponse";

import { ClientList } from "../models/client/clientList";
import { AddressCounty } from "../models/client/addclient/countyList";
import { AddressState } from "../models/client/addclient/stateList";

import { AddressCountryRegion } from "../models/client/addclient/countryRegionList";
import { City } from "../models/client/addclient/cityList";
import { UsersList } from "../models/users/users/usersList";
import { GetUserDto } from "../models/users/users/getUserDto";
import { Source } from "../models/shared/sourceList";
import { Currency } from "../models/shared/currencyList";
import { TypeList } from "../models/shared/typeList";
import { CustomerGroup } from "../models/client/addclient/customerGroupList";
import { ClientDetailDto } from "../models/client/details/clientDetailDto";

@Injectable({
  providedIn: "root",
})
export class AddNewClientService {
  private clientManagementBaseURL = environment.baseUrl + "/ClientManagement";
  private userManagementBaseURL = environment.baseUrl + "/User";
  private commonBaseURL = environment.baseUrl + "/Common";
  private fileManagementBaseURL = environment.baseUrl + "/fileManagement";
  private ProspectManagementBaseURL =
    environment.baseUrl + "/ProspectManagement";
  private locationBaseURL = environment.baseUrl + "/Location";
  private melissaBaseURL = environment.baseUrl + "/Melissa";
  private programBaseURL = environment.baseUrl + "/Program";
  private readonly getCountryRegionUrl =
    this.locationBaseURL + "/GetAllCountryRegion";
  private readonly countryRegionTtl = 8 * 60 * 60 * 1000; // 8 hours

  constructor(
    private _http: HttpClient,
    private _apiCache: ApiCacheService
  ) {}

  getClientLists(): Observable<GenericResponseDto<ClientList>> {
    return this._http.get<GenericResponseDto<ClientList>>(
      environment.baseUrl + "/"
    );
  }

  getTypeList(): Observable<GenericResponseDto<TypeList[]>> {
    return this._http.get<GenericResponseDto<TypeList[]>>(
      this.commonBaseURL + "/GetAllClientType"
    );
  }

  getBCList(): Observable<GenericResponseDto<UsersList[]>> {
    return this._http.get<GenericResponseDto<UsersList[]>>(
      this.userManagementBaseURL + "/GetAllBCUser"
    );
  }

  getBACList(id: number): Observable<GenericResponseDto<UsersList[]>> {
    return this._http.get<GenericResponseDto<UsersList[]>>(
      this.userManagementBaseURL + `/GetAllWhoSupportsMeByUserId?userid=${id}`
    );
  }

  getSourceList(): Observable<GenericResponseDto<Source[]>> {
    return this._http.get<GenericResponseDto<Source[]>>(
      this.clientManagementBaseURL + "/GetAllSources"
    );
  }

  getCustomerGroupList(): Observable<GenericResponseDto<CustomerGroup[]>> {
    return this._http.get<GenericResponseDto<CustomerGroup[]>>(
      this.clientManagementBaseURL + "/GetAllCustomerGroup"
    );
  }

  getParentCompanyNameList(): Observable<GenericResponseDto<Client[]>> {
    return this._http.get<GenericResponseDto<Client[]>>(
      this.clientManagementBaseURL + "/GetAllParentCompanyName"
    );
  }

  getCurrencyList(): Observable<GenericResponseDto<Currency[]>> {
    return this._http.get<GenericResponseDto<Currency[]>>(
      this.commonBaseURL + "/GetAllCurrency"
    );
  }

  getCountryRegionList(): Observable<
    GenericResponseDto<AddressCountryRegion[]>
  > {
    return this._apiCache.get<GenericResponseDto<AddressCountryRegion[]>>(
      this.getCountryRegionUrl,
      this.countryRegionTtl
    );
  }

  getAddressFormatsByFormatId(
    formatId: string
  ): Observable<GenericResponseDto<AddressFormats>> {
    return this._http.get<GenericResponseDto<AddressFormats>>(
      `${this.locationBaseURL}/GetAddressFormatsByFormatId?formatId=${formatId}`
    );
  }

  getZipList(
    id: string,
    name: string
  ): Observable<GenericResponseDto<string[]>> {
    return this._http.get<GenericResponseDto<string[]>>(
      this.locationBaseURL +
        `/AutoCompletGetPostalCodeByCountryAndPostalCodeValue?countryId=${id}&postalCode=${name}`
    );
  }

  // getDistrictList(id: string): Observable<GenericResponseDto<District[]>> {
  //   return this._http.get<GenericResponseDto<District[]>>(
  //     environment.baseUrl + '/'
  //   );
  // }

  getCityList(
    stateid: string,
    countryid: string
  ): Observable<GenericResponseDto<City[]>> {
    return this._http.get<GenericResponseDto<City[]>>(
      this.locationBaseURL +
        `/GetAllCitiesByCountryIdAndState?countryId=${countryid}&stateId=${stateid}`
    );
  }

  getCountyList(id: string): Observable<GenericResponseDto<AddressCounty[]>> {
    return this._http.get<GenericResponseDto<AddressCounty[]>>(
      this.locationBaseURL + "/GetAllCountiesByCountryId?countryId=" + id
    );
  }

  getStateList(id: string): Observable<GenericResponseDto<AddressState[]>> {
    return this._http.get<GenericResponseDto<AddressState[]>>(
      this.locationBaseURL + "/GetAllStatesByCountryId?countryId=" + id
    );
  }

  addClientData(
    payload: ClientDetailDto
  ): Observable<GenericResponseDto<ClientDetailDto>> {
    return this._http.post<GenericResponseDto<ClientDetailDto>>(
      this.clientManagementBaseURL + "/AddCustomer",
      payload
    );
  }

  addConvertProspectIntoClientData(
    payload: ClientDetailDto
  ): Observable<GenericResponseDto<ClientDetailDto>> {
    return this._http.post<GenericResponseDto<ClientDetailDto>>(
      this.ProspectManagementBaseURL + "/ConvertProspectIntoClient",
      payload
    );
  }

  getCustomerAccount(): Observable<
    GenericResponseDto<ClientAccountNumberResponse>
  > {
    return this._http.get<GenericResponseDto<ClientAccountNumberResponse>>(
      this.clientManagementBaseURL + "/GetClientAccountNumber"
    );
  }

  checkAddressValidation(
    address: string
  ): Observable<GenericResponseDto<boolean>> {
    return this._http.get<GenericResponseDto<boolean>>(
      this.clientManagementBaseURL + "/AddressValidation?address=" + address
    );
  }

  getRescaleAttachmentUrl(
    fileObj: FileUploadDto[]
  ): Observable<GenericResponseDto<string>> {
    return this._http.post<GenericResponseDto<string>>(
      this.fileManagementBaseURL + "/upload",
      fileObj[0]
    );
  }

  getTaxAttachmentUrl(
    fileObj: FileUploadDto[]
  ): Observable<GenericResponseDto<string>> {
    return this._http.post<GenericResponseDto<string>>(
      this.fileManagementBaseURL + "/upload",
      fileObj[0]
    );
  }

  getClientNameForAutoComplete(
    name: string
  ): Observable<GenericResponseDto<string[]>> {
    return this._http.get<GenericResponseDto<string[]>>(
      this.clientManagementBaseURL +
        "/AutoCompleteClientName?clientName=" +
        name
    );
  }

  GetAllConfiguredUser(): Observable<GenericResponseDto<UsersList[]>> {
    return this._http.get<GenericResponseDto<UsersList[]>>(
      this.userManagementBaseURL + "/GetAllConfiguredUser"
    );
  }

  // updateClientData(payload: any): Observable<GenericResponseDto<any>> {
  //   return this._http.post<GenericResponseDto<any>>(
  //     this.clientManagementBaseURL + '/',
  //     payload
  //   );
  // }

  getPostalCodeByZipCode(
    zipCode: string
  ): Observable<GenericResponseDto<ZipCodeDetails>> {
    return this._http.get<GenericResponseDto<ZipCodeDetails>>(
      `${this.commonBaseURL}/GetPostalCodeByZipCode?zipCode=${zipCode}`
    );
  }
  getMelissaData(street: string, country: string): Promise<[]> {
    return lastValueFrom(
      this._http.get<[]>(
        `${this.melissaBaseURL}/GetMelisaAPI?address=${street}&country=${country}`
      )
    );
  }

  getClientFreightAccountNumber(
    accountNumber: string
  ): Observable<GenericResponseDto<string>> {
    return this._http.get<GenericResponseDto<string>>(
      `${this.clientManagementBaseURL}/GetClientFreightAccountNumber?AccountNumber=${accountNumber}`
    );
  }

  getClientBillingDetails(
    customerAccount: string
  ): Observable<GenericResponseDto<ClientBillingDto>> {
    return this._http.get<GenericResponseDto<ClientBillingDto>>(
      `${this.clientManagementBaseURL}/GetClientBillingDetails?CustomerAccount=${customerAccount}`
    );
  }

  getCustomerFinancialDimension(
    customerAccount: string
  ): Observable<GenericResponseDto<string>> {
    return this._http.get<GenericResponseDto<string>>(
      `${this.clientManagementBaseURL}/GetCustomerFinancialDimension?CustomerAccount=${customerAccount}`
    );
  }

  GetMyProfile(): Observable<GenericResponseDto<GetUserDto>> {
    return this._http.get<GenericResponseDto<GetUserDto>>(
      this.userManagementBaseURL + "/GetMyProfile"
    );
  }

  GetProgramEmailDetails(
    accountNumber = "",
    programId = ""
  ): Observable<GenericResponseDto<GetProgramDetailsEmailDto>> {
    return this._http.get<GenericResponseDto<GetProgramDetailsEmailDto>>(
      `${this.programBaseURL}/GetProgramEmailDetails?CustomerAccount=${accountNumber}&ProgramId=${programId}`
    );
  }
}
